import { Fragment, useEffect, useState } from "react";

import { Link, useLocation } from "wouter";
import { Menu as MenuIcon } from "lucide-react";

import { useI18n } from "@/hooks/use-i18n";
import { useAuth } from "@/hooks/use-auth";
import { useLocalizedUrl } from "@/hooks/use-localized-url";
import { useAudioguidesAvailability } from "@/hooks/use-audioguides-availability";
import { useMenuCounts } from "@/hooks/use-menu-counts";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

import { CountPill } from "./count-pill";
import { MAIN_NAV, type NavCounts } from "./nav-data";
import { NewBadge } from "./new-badge";
import { useIsActive } from "./use-is-active";

// Menu mobile (shadcn Sheet da destra, Accordion per i gruppi). Stile del design system:
// voci 16px scure, fili sottili tra i gruppi, voce attiva azzurrina, nessuna icona decorativa.

// Voci top-level (link diretti, allineate ai trigger accordion): 16px, py-4.
const TOP_ITEM_CLASS =
  "flex min-h-[52px] w-full items-center justify-between rounded-lg px-4 py-3 text-left text-base font-medium text-ink transition-colors hover:bg-page";

// Voci di secondo livello dentro gli accordion: 14px, py-3.
const SUB_ITEM_CLASS =
  "flex min-h-[44px] w-full items-center justify-between rounded-lg px-4 py-2 text-left text-[15px] font-normal text-ink transition-colors hover:bg-page";

export function MobileMenu() {
  const { t, language } = useI18n();
  const getLocalizedUrl = useLocalizedUrl();
  const isActive = useIsActive();
  const { user, logoutMutation } = useAuth();
  const { data: menuCounts } = useMenuCounts();
  const {
    data: audioguides,
    isLoading: isLoadingAudioguides,
    error: audioguidesError,
  } = useAudioguidesAvailability();

  const [location, navigate] = useLocation();
  const locationWithoutLang = location.replace(/^\/(it|en|de|sl)/, "") || "/";
  const isAuthPage = locationWithoutLang === "/auth";

  const [isOpen, setIsOpen] = useState(false);
  const [mobileNavTouched, setMobileNavTouched] = useState(false);
  useEffect(() => {
    if (isOpen) setMobileNavTouched(false);
  }, [isOpen]);

  const close = () => setIsOpen(false);

  const showAudioguide =
    language !== "sl" &&
    (isLoadingAudioguides || audioguides?.available !== false || !!audioguidesError);

  const counts: NavCounts = { ...menuCounts, audioguides: audioguides?.count };

  return (
    <div className="flex items-center gap-2 md:hidden">
      <Sheet open={isOpen} onOpenChange={setIsOpen}>
        <SheetTrigger asChild>
          <button
            type="button"
            aria-label="Open menu"
            className="flex h-11 w-11 items-center justify-center rounded-lg text-white/90 transition-colors hover:bg-white/10 hover:text-white"
          >
            <MenuIcon className="h-5 w-5" />
          </button>
        </SheetTrigger>
        <SheetContent
          side="right"
          className="w-[320px] overflow-y-auto bg-white p-0 sm:max-w-sm"
        >
          <SheetHeader className="border-b bg-primary px-6 py-4">
            <SheetTitle
              className="text-left font-normal leading-tight tracking-[0.05em] text-white"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Trieste Storica
            </SheetTitle>
          </SheetHeader>

          <div className="flex flex-col px-2 pb-6 pt-6">
            {MAIN_NAV.map((entry, index) => {
              const divider = index > 0 ? <div className="mx-4 border-b border-line-subtle" /> : null;

              if (entry.kind === "menu") {
                const menu = {
                  ...entry.menu,
                  items: entry.menu.items.filter((item) => !item.requiresAudioguides || showAudioguide),
                };
                const groupActive = menu.activePaths.some((path) => isActive(path));
                return (
                  <Fragment key={menu.key}>
                    {divider}
                    <Accordion type="single" collapsible>
                      <AccordionItem value={menu.key} className="border-none">
                        <AccordionTrigger
                          className={`min-h-[52px] rounded-lg px-4 py-3 text-base font-medium leading-normal tracking-normal text-ink no-underline hover:bg-page hover:no-underline data-[state=open]:bg-page ${
                            !mobileNavTouched && groupActive ? "bg-surface-subtle" : ""
                          }`}
                          onClick={() => setMobileNavTouched(true)}
                        >
                          {t(menu.triggerKey)}
                        </AccordionTrigger>
                        <AccordionContent className="pb-4 pt-2">
                          <div className="flex flex-col gap-1 pl-2">
                            {menu.items.map((item) => {
                              const active = isActive(item.path);
                              return (
                                <Link key={item.key} href={getLocalizedUrl(item.path)}>
                                  <button
                                    type="button"
                                    onClick={close}
                                    className={`${SUB_ITEM_CLASS} ${active ? "bg-surface-subtle text-action-hover" : ""}`}
                                  >
                                    <span className="flex-1 text-left">{t(item.labelKey)}</span>
                                    <CountPill
                                      count={item.countKey ? counts[item.countKey] : undefined}
                                      active={active}
                                      language={language}
                                    />
                                  </button>
                                </Link>
                              );
                            })}
                          </div>
                        </AccordionContent>
                      </AccordionItem>
                    </Accordion>
                  </Fragment>
                );
              }

              const { link } = entry;
              if (link.italianOnly && language !== "it") return null;
              return (
                <Fragment key={link.key}>
                  {divider}
                  <Link href={getLocalizedUrl(link.path)}>
                    <button
                      type="button"
                      onClick={close}
                      className={`${TOP_ITEM_CLASS} ${isActive(link.path) ? "bg-surface-subtle text-action-hover" : ""}`}
                    >
                      <span>{t(link.labelKey)}</span>
                      {link.isNew && <NewBadge />}
                    </button>
                  </Link>
                </Fragment>
              );
            })}

            {/* SEZIONE ACCOUNT UTENTE — ChakraHeader:872-998 */}
            {user && (
              <>
                <div className="mb-4 mt-6 border-t border-line-subtle" />
                <div className="mb-2 px-4">
                  <p className="text-[11.5px] font-semibold uppercase tracking-[0.07em] text-ink-muted">
                    {t("profile.menu.account") || "Il tuo account"}
                  </p>
                </div>

                <Link href={`/${language}/user/profile-page`}>
                  <button
                    type="button"
                    onClick={close}
                    className={`${TOP_ITEM_CLASS} ${
                      isActive("/user/profile-page") ? "bg-surface-subtle text-action-hover" : ""
                    }`}
                  >
                    <span>{t("profile.title")}</span>
                  </button>
                </Link>
                <div className="mx-4 border-b border-line-subtle" />
                <Link href={`/${language}/user/favorites`}>
                  <button
                    type="button"
                    onClick={close}
                    className={`${TOP_ITEM_CLASS} ${
                      isActive("/user/favorites") ? "bg-surface-subtle text-action-hover" : ""
                    }`}
                  >
                    <span>{t("profile.favorites.title")}</span>
                  </button>
                </Link>
                <div className="mx-4 border-b border-line-subtle" />
                <Link href={`/${language}/user/bookmarks`}>
                  <button
                    type="button"
                    onClick={close}
                    className={`${TOP_ITEM_CLASS} ${
                      isActive("/user/bookmarks") ? "bg-surface-subtle text-action-hover" : ""
                    }`}
                  >
                    <span>{t("profile.bookmarks.title") || "Segnalibri"}</span>
                  </button>
                </Link>

                {user.isAdmin && (
                  <>
                    <div className="mx-4 border-b border-line-subtle" />
                    <button
                      type="button"
                      onClick={() => {
                        navigate("/admin");
                        close();
                      }}
                      className={`${TOP_ITEM_CLASS} ${
                        isActive("/admin") ? "bg-surface-subtle text-action-hover" : ""
                      }`}
                    >
                      <span>{t("admin.dashboard")}</span>
                    </button>
                  </>
                )}

                <div className="mx-4 border-b border-line-subtle" />
                <button
                  type="button"
                  onClick={() => {
                    close();
                    logoutMutation.mutate();
                  }}
                  className={TOP_ITEM_CLASS}
                >
                  <span>{t("auth.logout")}</span>
                </button>
              </>
            )}

            {/* CTA auth — ChakraHeader:1019-1034 */}
            {!user && !isAuthPage && (
              <>
                <div className="mx-4 mb-2 mt-2 border-t border-line-subtle pt-4" />
                <Link href={getLocalizedUrl("/auth")} aria-label={t("auth.login")}>
                  <button
                    type="button"
                    onClick={close}
                    className="h-11 w-full rounded-lg border border-line-strong bg-white px-4 text-sm font-medium text-ink transition-colors hover:border-line hover:bg-page"
                  >
                    {t("auth.login")}
                  </button>
                </Link>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
